import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import ProjectCard from "../../../components/common/ProjectCard";
import { SectionHeader, Reveal } from "../../../components/common";
import { featuredProjects } from "../../../config/constants/projectData";
import { getProjectUrl } from "../../../utils/slugify";

const ProjectsSection = () => {
  const [spotlight, ...rest] = featuredProjects;

  return (
    <section className="py-16 md:py-24">
      <SectionHeader
        index="03"
        eyebrow="The highlight reel"
        title={<>Made with obsession. Shipped with pride.</>}
        lede="Four favorites pulled from 120+ launches: identities, campaigns and digital products crafted in Chattogram and loved far beyond it."
        layout="split"
        action={
          <Link
            to="/projects"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white bg-designer-dark hover:bg-designer-primary transition-colors"
          >
            Browse the full archive
            <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        }
      />

      {/* Spotlight: the latest obsession, full width */}
      {spotlight && (
        <Reveal className="mt-10 md:mt-14">
          <article
            className="group grid overflow-hidden rounded-[2rem] border border-designer-dark/10 bg-white transition-shadow duration-300 hover:shadow-[0_30px_70px_-30px_rgba(29,32,48,0.4)] md:grid-cols-2"
            itemScope
            itemType="https://schema.org/CreativeWork"
          >
            <div className="relative min-h-[260px] overflow-hidden md:min-h-[380px]">
              <Link
                to={getProjectUrl(spotlight.slug)}
                aria-label={`View details for ${spotlight.title} project`}
                tabIndex={-1}
              >
                <img
                  src={spotlight.image}
                  alt={`${spotlight.title} - ${spotlight.category} project by Arvix Creative`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </Link>
              <span
                className="absolute left-5 top-5 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-[0.14em] uppercase bg-white/90 backdrop-blur text-designer-dark"
                itemProp="genre"
              >
                {spotlight.category}
              </span>
            </div>
            <div className="relative flex flex-col justify-center p-7 md:p-10 lg:p-12">
              <span
                className="numeral-outline pointer-events-none absolute right-6 top-4 hidden font-display text-7xl font-extrabold leading-none sm:block md:text-8xl"
                aria-hidden="true"
              >
                01
              </span>
              <p className="flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-designer-primary">
                <span className="tick" aria-hidden="true" />
                Fresh from the studio floor
              </p>
              <h3
                className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-designer-dark md:text-4xl"
                itemProp="name"
              >
                {spotlight.title}
              </h3>
              <p
                className="mt-3 max-w-lg leading-relaxed text-gray-600"
                itemProp="description"
              >
                {spotlight.description}
              </p>
              <div className="mt-7">
                <Link
                  to={getProjectUrl(spotlight.slug)}
                  className="group/btn inline-flex items-center gap-2 rounded-full bg-designer-primary px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-700"
                  aria-label={`Learn more about ${spotlight.title} project`}
                >
                  Peek inside the case
                  <FaArrowRight className="h-3.5 w-3.5 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </article>
        </Reveal>
      )}

      {/* The supporting cast */}
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 md:mt-6 md:gap-6 lg:grid-cols-3">
        {rest.map((project, i) => (
          <Reveal key={project.id} delay={(i % 3) * 110} className="h-full">
            <ProjectCard project={project} index={i + 1} />
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default ProjectsSection;
